import {useObservable} from '@ngneat/react-rxjs';
import {useSnackbar} from 'notistack';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import {finalize, switchMap} from 'rxjs';

import {clientDetailsQuery, clientDetailsService} from '@store/clients/details';
import {ManageContact, ManageContactType} from '@store/common/organisation.model';

import OptionWithAlertComponent from '@components/moreOptions/OptionWithAlert.component';
import HeadOfficeForm from '@components/unitDetails/HeadOffice.form';
import ManageAddressDrawer from '@components/unitDetails/ManageAddress.drawer';
import ManageBillingMethodDrawer from '@components/unitDetails/ManageBillingMethod.drawer';
import ManageContactDrawer from '@components/unitDetails/ManageContact.drawer';
import {FacturationMode} from '@store/clients/details/facturationMode/facturation-mode.model';
import {clientsUnitsService} from '@store/clients/units';
import {GlobalStatus} from '@store/common/globalStatus.enum';

const HeadOfficeTab = () => {
  const {t} = useTranslation();
  const {clientId} = useParams();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();

  const [loading, setLoading] = useState(false);

  const [openContact, setOpenContact] = useState<ManageContact | boolean>(false);
  const [openAddress, setOpenAddress] = useState<ManageContact | boolean>(false);
  const [openFacturationMode, setOpenFacturationMode] = useState<FacturationMode | undefined>();

  const [{headOffice}] = useObservable(clientDetailsQuery.clientDetails$);
  const [{loading: loadingFacturationMode}] = useObservable(clientDetailsQuery.putClientFacturationMode$);

  useEffect(() => {
    if (clientId) {
      clientDetailsService.getClientById(clientId).subscribe({
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  }, [clientId, enqueueSnackbar]);

  const handleClose = () => {
    navigate('../..', {replace: true});
  };

  const isArchived = clientDetailsQuery.isArchived;

  const handleEdit = (client) => {
    if (!clientId) return;
    setLoading(true);
    clientDetailsService.updateClient(clientId, client).pipe(finalize(() => setLoading(false)))
      .subscribe({
        next: () => {
          enqueueSnackbar(t('clients.success.edit'), {variant: 'success'});
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
  };

  const handleDeleteContact = (type: ManageContactType, organisationId, contactId, successCallback) => {
    if (!organisationId || !contactId) return;
    setLoading(true);
    switch (type) {
      case ManageContactType.ORGANISATION:
        clientDetailsService.deleteClientContact(organisationId, contactId).pipe(
          finalize(() => setLoading(false)),
          switchMap(() => clientDetailsService.getClientById(clientId ?? organisationId))
        )
          .subscribe({
            next: () => {
              enqueueSnackbar(t('delete.success.contact'), {variant: 'success'});
              successCallback();
            },
            error: (err) => enqueueSnackbar(err.text, err.options),
          });
        break;
      case ManageContactType.ORGANISATION_UNIT:
        clientsUnitsService.deleteClientsUnitContact(organisationId, contactId).pipe(
          finalize(() => setLoading(false)),
          switchMap(() => clientDetailsService.getClientById(clientId ?? organisationId))
        )
          .subscribe({
            next: () => {
              enqueueSnackbar(t('delete.success.contact'), {variant: 'success'});
              successCallback();
            },
            error: (err) => enqueueSnackbar(err.text, err.options),
          });
        break;
      default:
        clientDetailsService.deleteClientContact(organisationId, contactId).pipe(
          finalize(() => setLoading(false)),
          switchMap(() => clientDetailsService.getClientById(clientId ?? organisationId))
        )
          .subscribe({
            next: () => {
              enqueueSnackbar(t('delete.success.contact'), {variant: 'success'});
              successCallback();
            },
            error: (err) => enqueueSnackbar(err.text, err.options),
          });
    }
  };


  const handleDeleteAddress = (addressId) => {
    console.log(addressId);
  };


  return (
    <>
      <HeadOfficeForm
        organisation={headOffice}
        handleSave={handleEdit}
        handleManageContact={setOpenContact}
        handleFacturationMode={setOpenFacturationMode}
        handleDeleteContact={handleDeleteContact}
        handleManageAddress={setOpenAddress}
        handleDeleteAddress={handleDeleteAddress}
        loading={loading}
        moreOptions={[
          <OptionWithAlertComponent
            key="manage status"
            alertTitle={t(`${isArchived ? 'unarchive' : 'archive'}.title`)}
            alertDescription={t(`${isArchived ? 'unarchive' : 'archive'}.description.client`)}
            validateTitle={t(`global.${isArchived ? 'unarchive' : 'archive'}`)}
            handleSuccess={() => {
              enqueueSnackbar(t(`${isArchived ? 'unarchive' : 'archive'}.success.client`), {variant: 'success'});
            }}
            handleValidate={clientId
              ? () => clientDetailsService.updateClientStatus(clientId, isArchived ? GlobalStatus.ACTIVE : GlobalStatus.ARCHIVED)
              : undefined}
            label={t(`global.${isArchived ? 'unarchive' : 'archive'}`)}/>,
          <OptionWithAlertComponent
            key="delete"
            alertTitle={t('delete.title')}
            alertDescription={t('delete.description.client')}
            validateTitle={t('global.delete')}
            handleSuccess={() => {
              enqueueSnackbar(t('delete.success.client'), {variant: 'success'});
              handleClose();
            }}
            handleValidate={clientId ? () => clientDetailsService.deleteClient(clientId) : undefined}
            label={t('global.delete')}/>
        ]}
        readOnly={isArchived}/>
      {!!openAddress && (
        <ManageAddressDrawer
          address={typeof openAddress !== 'boolean' ? openAddress : undefined}
          readOnly={isArchived}
          handleClose={() => setOpenAddress(false)}
          handleAdd={(address) => {
            if (address.clientInvoiceAddressId && (address.emails || address.ccEmails) && clientId)
              clientDetailsService.updateInvoiveAddress(address.clientInvoiceAddressId, address.emails || [], address.ccEmails || [], clientId).subscribe({
                next: () => {
                  enqueueSnackbar(t('unitDetails.success.address.edit'), {variant: 'success'});
                }
              });
          }}/>
      )}
      {!!openContact && (
        <ManageContactDrawer
          contact={typeof openContact !== 'boolean' ? openContact : undefined}
          readOnly={isArchived}
          handleClose={() => setOpenContact(false)}
          handleAdd={(contact) => {
            if (!clientId) return;

            if (typeof openContact === 'boolean') {
              clientDetailsService.createClientContact(clientId, contact).subscribe({
                next: () => {
                  enqueueSnackbar(t('unitDetails.success.contact.create'), {variant: 'success'});
                  setOpenContact(false);
                },
                error: (err) => enqueueSnackbar(err.text, err.options),
              });
            } else {
              if (!contact.id) return;
              clientDetailsService.updateClientContact(clientId, contact.id, contact).subscribe({
                next: () => {
                  enqueueSnackbar(t('unitDetails.success.contact.edit'), {variant: 'success'});
                },
                error: (err) => enqueueSnackbar(err.text, err.options),
              });
            }
          }}/>
      )}
      {!!openFacturationMode && (
        <ManageBillingMethodDrawer
          handleAdd={(updatedData) => {
            if (!clientId) return;
            clientDetailsService.updateClientFacturationMode(clientId, openFacturationMode.id, openFacturationMode, updatedData).subscribe({
              next: () => {
                enqueueSnackbar(t('unitDetails.billingMethod.success'), {variant: 'success'});
                setOpenFacturationMode(undefined);
              },
              error: (err) => enqueueSnackbar(err.text, err.options),
            });
          }}
          handleClose={() => setOpenFacturationMode(undefined)}
          facturationMode={openFacturationMode}
          loading={loadingFacturationMode}
        />
      )}
    </>
  );
};

export default HeadOfficeTab;
